/** @jsxRuntime classic */
/** @jsx jsx */
import { gql, useQuery } from '@apollo/client';
import { Title } from '@bottlebooks/gatsby-design-system';
import { Col, Container, Row } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { jsx } from 'theme-ui';
import useLink from '../../useLink';
import BrandAddressSection, {
  BrandAddressSectionFragment,
} from '../Brand/BrandAddressSection';
import BrandBanner, { BrandBannerFragment } from '../Brand/BrandBanner';
import BrandShopsSection, {
  BrandShopsSectionFragment,
} from '../Brand/BrandShopsSection';
import ContentRow from '../ContentRow';
import ExhibitorActions from '../Exhibitor/ExhibitorActions';
import ExhibitorHeaderRow, {
  ExhibitorHeaderRowFragment,
} from '../ExhibitorPage/ExhibitorHeaderRow';
import ExhibitorSections from '../ExhibitorPage/ExhibitorSections';
import PreviewLayout from '../Layout/PreviewLayout';
import ProductListListRow from '../ProductListsList/ProductListListRow';
import ProductListsList from '../ProductListsList/ProductListsList';
import ProductListRow from '../ProductsList/ProductListRow';
import ProductsList from '../ProductsList/ProductsList';

export default function ExhibitorPreviewPage({
  exhibitorId,
  event,
  locale,
  productLists,
}) {
  const link = useLink();
  const { data, loading, error /* , refetch */ } = useQuery(clientQuery, {
    variables: { exhibitorId, eventId: event?.eventId },
  });
  if (loading) return <PreviewLayout locale={locale}>Loading…</PreviewLayout>;
  if (error) {
    console.error(error);
    return (
      <PreviewLayout locale={locale}>
        We're sorry, but there was an error.
      </PreviewLayout>
    );
  }

  const exhibitor = data?.event?.exhibitor;
  const products = exhibitor?.products?.nodes || {};
  const hasProducts = products && products.length !== 0;
  const hasProductLists = productLists && productLists.length !== 0;
  const hasProductsSection = hasProducts || hasProductLists;
  return (
    <PreviewLayout locale={locale} sx={{ backgroundColor: 'transparent' }}>
      <BrandBanner {...exhibitor} />
      <ExhibitorHeaderRow exhibitor={exhibitor} />

      <Container>
        <ContentRow sx={{ paddingY: 3 }}>
          <Col>
            <ExhibitorActions exhibitor={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      <ExhibitorSections exhibitor={exhibitor} eventId={event.eventId} />

      <BrandAddressSection brand={exhibitor} />

      <Container>
        <ContentRow>
          <Col>
            <BrandShopsSection brand={exhibitor} />
          </Col>
        </ContentRow>
      </Container>

      {hasProductsSection ? (
        <Container>
          <Row
            sx={{
              marginY: 5,
              paddingX: [null, `${100 / 12}%`], // 1 col spacing from medium width up.
            }}
          >
            <Col flex>
              <Title sx={{ marginBottom: 4 }}>
                <Trans>Products</Trans>
              </Title>
              {hasProductLists && (
                <ProductListsList sx={{ marginX: [-3, -2], marginY: -2 }}>
                  {productLists.map((productList) => (
                    <ProductListListRow
                      key={productList.id}
                      productList={productList}
                      to={link.productList(productList)}
                      sx={{ marginX: [0, 2], marginY: [0, 2] }}
                    />
                  ))}
                </ProductListsList>
              )}

              {hasProducts && (
                <ProductsList sx={{ marginX: [-3, -2], marginY: -2 }}>
                  {products.map((product) => (
                    <ProductListRow
                      key={product.id}
                      product={product}
                      to={link.product(product)}
                      sx={{ marginX: [0, 2], marginY: [0, 2] }}
                    />
                  ))}
                </ProductsList>
              )}
            </Col>
          </Row>
        </Container>
      ) : null}
    </PreviewLayout>
  );
}

const clientQuery = gql`
  query ExhibitorPreviewPage($eventId: ID!, $exhibitorId: ID!) {
    # TODO this should query the Bottlebooks services instead, then the query root would be the event!
    event(eventId: $eventId) {
      eventId # used for the cache.
      exhibitor(exhibitorId: $exhibitorId) {
        exhibitorId # used for calculating the cache entry.
        ...BrandBanner
        ...BrandAddressSection
        ...ExhibitorHeaderRow
        ...ExhibitorActions
        ...ExhibitorSections
        ...BrandShopsSection
        products {
          nodes {
            productId # used for the cache.
            id: productId
            slug: productId
            ...useLink_Product
            producer {
              producerId # used for the cache.
            }
            ... on ExtendedProduct {
              exhibitor {
                exhibitorId # needed for the apolloClient cache.
              }
            }
            ...ProductListRow
          }
        }
      }
    }
  }
  ${BrandBannerFragment}
  ${BrandAddressSectionFragment}
  ${ExhibitorHeaderRowFragment}
  ${ExhibitorActions.fragment}
  ${ExhibitorSections.fragment}
  ${BrandShopsSectionFragment}
  ${ProductListRow.fragment}
  ${useLink.productFragment}
`;
